// 프레임 이미지 → 애니 스트립(가로 정사각 N프레임) 조립 공용 로직.
// AnimationStudio(전체 탭)와 StripBuilder(메인 캐릭터 인라인)가 같은 규격을 쓰도록 단일 정의.
// 규격: 정사각 FRAME px, 캐릭터 높이 CONTENT_H 로 정규화 후 하단 정렬(프레임 간 크기·베이스라인 일관).

export const FRAME = 512; // 프레임 한 변(px)
export const CONTENT_H = 460; // 프레임 내 캐릭터 높이(하단 정렬)

/** 알파 bbox 트림 + 높이 정규화 + 정사각 하단정렬 → 프레임 캔버스. */
export function normalizeFrame(
  img: HTMLImageElement | HTMLCanvasElement,
  /** 프레임 한 변(px). 형제와 크기를 맞출 때만 다르게 준다(기본은 이 저장소 규격). */
  frame: number = FRAME,
): HTMLCanvasElement {
  const w = img instanceof HTMLImageElement ? img.naturalWidth : img.width;
  const h = img instanceof HTMLImageElement ? img.naturalHeight : img.height;
  const src = document.createElement("canvas");
  src.width = w;
  src.height = h;
  const sctx = src.getContext("2d")!;
  sctx.drawImage(img, 0, 0);
  const { data } = sctx.getImageData(0, 0, w, h);
  let minX = w, minY = h, maxX = -1, maxY = -1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (data[(y * w + x) * 4 + 3]! > 8) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < 0) { minX = 0; minY = 0; maxX = w - 1; maxY = h - 1; }
  const bw = maxX - minX + 1;
  const bh = maxY - minY + 1;
  /**
   * 🔴 캐릭터 높이·바닥 여백은 **프레임에 비례**해야 한다.
   * `CONTENT_H`·`8` 은 512px 프레임 기준 절대값이라, 128px 프레임에 그대로 쓰면
   * 캐릭터가 프레임보다 커지고 바닥이 어긋난다(형제와 나란히 놓으면 바로 티가 난다).
   */
  const k = frame / FRAME;
  const contentH = CONTENT_H * k;
  const pad = 8 * k;
  const scale = contentH / bh;
  const dw = Math.round(bw * scale);
  const out = document.createElement("canvas");
  out.width = frame;
  out.height = frame;
  const octx = out.getContext("2d")!;
  octx.imageSmoothingQuality = "high";
  octx.drawImage(src, minX, minY, bw, bh, Math.round((frame - dw) / 2), frame - contentH - pad, dw, contentH);
  return out;
}

/** 교차출처 이미지 로드(에셋 서버는 CORS 공개 — 캔버스 판독 가능). */
export function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((res, rej) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => res(img);
    img.onerror = () => rej(new Error("이미지 로드 실패"));
    img.src = url;
  });
}

/**
 * 🔄 **서버의 지금 그림으로** 불러온다 — 브라우저가 붙든 옛 판이 아니라.
 *
 * 🔴 2026-09-14 운영자 신고 「옆으로 걷는데 두 번째 프레임이 방향이 다르다」. 운영 게시본은 새 판(네 칸 모두 오른쪽,
 *    파일 해시까지 저장소와 같음)이었는데, 편집 화면은 **같은 주소의 옛 판**(칸마다 방향이 번갈던 것)을 풀어 보여 줬다.
 *    `/art/…` 는 파일 이름이 고정이라 브라우저가 한 시간 붙든다(_headers `max-age=3600`). 편집 화면이 옛 판을 풀어
 *    다시 게시하면 **고친 그림이 도로 옛것으로** 나간다 — 그래서 여기서는 반드시 서버에 다시 물어본다(`no-cache`).
 * `data:`·`blob:` 은 이미 손에 든 그림이라 그대로.
 * 못 받으면 던진다 — 옛 판으로 조용히 넘어가지 않는다(그게 이 사고의 모양이다).
 */
export async function loadFreshImage(url: string): Promise<HTMLImageElement> {
  if (/^(data|blob):/i.test(url)) return loadImage(url);
  const res = await fetch(url, { cache: "no-cache" });
  if (!res.ok) throw new Error(`그림을 못 받았어요(${res.status})`);
  const obj = URL.createObjectURL(await res.blob());
  try {
    return await loadImage(obj);
  } finally {
    URL.revokeObjectURL(obj); // 불러온 뒤엔 그림이 이미 풀려 있다 — 주소만 치운다
  }
}

/** 게시된 스트립 → 프레임 이미지들로 슬라이스(편집용 복원). 🔄 서버의 지금 판으로(`loadFreshImage`). */
export async function sliceStrip(url: string): Promise<HTMLImageElement[]> {
  const img = await loadFreshImage(url);
  const fh = img.naturalHeight;
  const n = Math.max(1, Math.round(img.naturalWidth / fh));
  const out: HTMLImageElement[] = [];
  for (let i = 0; i < n; i++) {
    const cv = document.createElement("canvas");
    cv.width = fh;
    cv.height = fh;
    cv.getContext("2d")!.drawImage(img, i * fh, 0, fh, fh, 0, 0, fh, fh);
    const fi = new Image();
    await new Promise<void>((res) => {
      fi.onload = () => res();
      fi.src = cv.toDataURL("image/png");
    });
    out.push(fi);
  }
  return out;
}

/** 프레임들 → 가로 스트립 webp Blob(정규화·하단정렬 포함). */
export async function assembleStripBlob(
  frames: Array<HTMLImageElement | HTMLCanvasElement>,
  /**
   * 프레임 한 변(px). 기본은 이 저장소 규격(`FRAME`).
   *
   * 🔴 **형제와 맞출 때만** 다르게 준다. 같은 걷기 띠를 만드는 길이 둘이라
   * (`scripts/slice-walk-sheet.cjs` 는 128px, 여기는 512px) 한 방향만 8배로 무거워진
   * 적이 있다 — 그때 그 한 장을 형제 크기로 다시 만드는 데 쓴다.
   */
  frame: number = FRAME,
): Promise<Blob> {
  const strip = document.createElement("canvas");
  strip.width = frame * frames.length;
  strip.height = frame;
  const ctx = strip.getContext("2d")!;
  frames.forEach((f, i) => ctx.drawImage(normalizeFrame(f, frame), i * frame, 0));
  const blob = await new Promise<Blob | null>((res) => strip.toBlob(res, "image/webp", 0.92));
  if (!blob) throw new Error("스트립 생성 실패");
  return blob;
}

/** 파일 → 이미지 엘리먼트(로컬 미리보기용). */
export function fileToImage(file: File): Promise<HTMLImageElement> {
  return new Promise((res, rej) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => res(img);
    img.onerror = () => rej(new Error("이미지를 읽을 수 없어요"));
    img.src = url;
  });
}
